import { formatNearAmount } from '@near-js/utils';
import { Link } from '@react-pdf/renderer';
import BN from 'bn.js';

import PinIcon from 'components/icons/PinIcon';
import DeadlineClockIcon from 'components/icons/DeadlineClockIcon';

import useTimer from 'lib/hooks/use-timer';
import { formatTokenAmount } from 'utils/amounts';
import { nanoSecondsToDate, timerCountdown, calculationCell } from 'utils';
import styles from 'components/certificates-table/styles';

import config from 'config';
const { EXPLORER_URL } = config;

import {
  StyledCapitalizeText,
  StyledValueWithIcon,
  StyledSuccessText,
  StyledErrorText,
  StyledLink,
  StyledLine,
  StyledValueImage
} from './grid-table.style';

export const useNormalizeNestedCell = (
  item,
  dataIndex: string,
  pdf = false
) => {
  let cellValue: JSX.Element | string;
  const value = item[dataIndex];

  switch (dataIndex) {
    case 'value':
      cellValue = `${formatTokenAmount(value, 24, 5)} tCO2e`;
      break;
    case 'name':
      if (pdf) {
        cellValue = (
          <Link style={styles.link} src={`${EXPLORER_URL}/${item.project_id}`}>
            {value}
          </Link>
        );
      } else {
        cellValue = (
          <StyledLink
            href={`${EXPLORER_URL}/${item.project_id}`}
            target="_blank"
          >
            {value}
          </StyledLink>
        );
      }
      break;
    case 'type':
      if (value === 'conservation') {
        cellValue = 'Forest Conservation';
      } else if (value === 'reforestation') {
        cellValue = 'Afforestation / Reforestation';
      } else if (value === 'agroforestry') {
        cellValue = 'Agroforestry';
      } else if (value === 'regeneration') {
        cellValue = 'Assisted Natural Regeneration';
      } else if (value === 'management') {
        cellValue = 'Improved Forest Management';
      }
      break;
    default:
      cellValue = <>{value}</>;
      break;
  }

  return { cellValue };
};

export const useNormalizeCell = (item, dataIndex: string) => {
  const className = '';
  const noData = '---';
  const value = item[dataIndex];
  let cellValue: JSX.Element | string;
  const stageTimeleft = useTimer(
    dataIndex === 'deadline' ? item[dataIndex] / 1e6 : 0
  );

  switch (dataIndex) {
    case 'currentStage':
      const showStage = value + 1;
      cellValue = value === -1 ? 'None' : <>{showStage}</>;
      break;
    case 'region':
      cellValue = (
        <StyledValueWithIcon>
          <PinIcon color="#c4c4c4" />
          {value}
        </StyledValueWithIcon>
      );
      break;
    case 'starts_at':
    case 'issued_at':
      cellValue = value === null ? noData : nanoSecondsToDate(value);
      break;
    case 'myOpnRewards':
      cellValue = `${formatTokenAmount(value, 24, 5)} OPN`;
      break;
    case 'stakedAmount':
      cellValue = `${formatNearAmount(value, 5)} NEAR`;
      break;
    case 'token_amount':
    case 'offset_amount':
      cellValue = formatTokenAmount(value, 24, 5);
      break;
    case 'parsedSquare':
      cellValue = value ? <>{value}</> : <>{noData}</>;
      break;
    case 'deadline':
      cellValue = (
        <StyledValueWithIcon>
          <DeadlineClockIcon color="#c4c4c4" />
          {timerCountdown(stageTimeleft)}
        </StyledValueWithIcon>
      );
      break;
    case 'media':
      cellValue = <StyledValueImage src={value} />;
      break;
    case 'statusState':
      cellValue = (
        <StyledCapitalizeText>{value.toLowerCase()}</StyledCapitalizeText>
      );
      break;
    case 'status':
      if (value === 'OPEN') {
        cellValue = <StyledSuccessText>{value}</StyledSuccessText>;
      }
      if (value === 'CLOSED') {
        cellValue = <StyledErrorText>{value}</StyledErrorText>;
      }
      break;
    case 'spaceLeft':
    case 'total':
    case 'rewards':
      cellValue = `${value} OPN`;
      break;
    case 'estimated':
      cellValue = `${value}%`;
      break;
    case 'validationStatus':
      const { valueCell } = calculationCell(item);
      cellValue = valueCell;
      break;
    case 'validationRemaining':
      const { valueDate } = calculationCell(item);
      cellValue = (
        <StyledValueWithIcon>
          <DeadlineClockIcon color="#c4c4c4" />
          {timerCountdown(valueDate)}
        </StyledValueWithIcon>
      );
      break;
    case 'validationStake':
      cellValue = (
        <>
          <StyledLine>{item.amountAffirm.value} OPN - AFFIRM</StyledLine>
          <StyledLine>{item.amountDeny.value} OPN - DENY</StyledLine>
        </>
      );
      break;
    case 'validationRewards':
      let totalValue = 0;
      const stage = item.stages[item.key.stage_id];
      const last_period = stage.last_completed_period;
      const current_period = stage.periods[last_period];
      const comparisonResult = new BN(current_period.voting_affirm_amount).cmp(
        new BN(current_period.voting_deny_amount)
      );

      totalValue =
        comparisonResult === -1
          ? ((+stage.reward_amount / 1e24) * item.amountDeny.value) /
            (+stage.deny_stake / 1e24)
          : ((+stage.reward_amount / 1e24) * item.amountAffirm.value) /
            (+stage.affirm_stake / 1e24);

      cellValue = `${formatTokenAmount(totalValue.toString(), 0, 5)} OPN`;
      break;
    default:
      cellValue = <>{value}</>;
      break;
  }
  return { className, cellValue };
};
